import { useQuery } from "@tanstack/react-query";
import { type KeyboardEvent, type RefObject, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useMemoryCapabilities } from "../customers/access";
import { CUSTOMER_LIST_MAX, memoryKeys } from "../customers/queries";
import { useOptionalMemoryRuntime } from "../customers/runtime";
import { navIconFor } from "../navigation/nav-icons";
import type { AdminArea } from "../navigation/offering";
import { cn } from "../ui/cn";
import { Icon } from "../ui/icon";
import { Input } from "../ui/input";
import { SearchDialog } from "../ui/search-dialog";
import { type PaletteResult, paletteResults } from "./palette-results";

// The search dialog of the top bar (⌘K or Ctrl+K, or a click on "Search anything"). It jumps to a page the staff member is offered, or to a
// customer they may open. It is a combobox: the input keeps focus, the arrow keys move the highlighted option (`aria-activedescendant`),
// Enter opens it, Escape closes the dialog. The customers come from the list the Customers page reads (`GET /staff/customers`, up to 100,
// filtered here); it is read only while the dialog is open and only for a role that may list customers, and nothing is kept afterwards.

type CommandPaletteProps = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The control that had focus when the dialog opened. A search that ends without a jump gives focus back to it. */
  opener: RefObject<HTMLElement | null>;
  /** The pages offered to this staff member, in menu order. */
  areas: readonly AdminArea[];
}>;

/** Keeps the highlighted option in view when the arrow keys move past the edge of the list. */
const keepInView = (node: HTMLElement | null) => node?.scrollIntoView?.({ block: "nearest" });

const GROUP_LABELS = { page: "Pages", customer: "Customers" } as const;

export function CommandPalette({ open, onOpenChange, opener, areas }: CommandPaletteProps) {
  // A jump to another page puts focus on that page (a person who follows a result starts reading the new page); a search that ends with
  // Escape puts it back where it was. Radix has no trigger to return to here, so both are done by hand.
  const jumped = useRef(false);
  return (
    <SearchDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search Lafayette Staff"
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        const target = jumped.current ? document.getElementById("main-content") : opener.current;
        jumped.current = false;
        target?.focus();
      }}
    >
      <PaletteBody
        areas={areas}
        close={(didJump) => {
          jumped.current = didJump;
          onOpenChange(false);
        }}
      />
    </SearchDialog>
  );
}

/** The highlight after a key, or `null` for a key that does not move it. The arrow keys wrap around. Home and End stay with the text caret. */
function nextHighlight(key: string, highlighted: number, count: number): number | null {
  const last = count - 1;
  switch (key) {
    case "ArrowDown":
      return highlighted >= last ? 0 : highlighted + 1;
    case "ArrowUp":
      return highlighted <= 0 ? last : highlighted - 1;
    default:
      return null;
  }
}

/** The line under the list: what the person cannot see in the list. `null` when the list says it all. */
function statusLine(
  resultCount: number,
  customers: Readonly<{ pending: boolean; failed: boolean }>,
): string | null {
  if (customers.pending) return "Looking for customers…";
  if (customers.failed) return "Customers could not be read. Pages are still listed.";
  return resultCount === 0 ? "Nothing matches. Try another word." : null;
}

/** The list of customers the staff member may open, read only while the dialog is open and only for a role that may list them. */
function useCustomerRead(typing: boolean) {
  const memory = useOptionalMemoryRuntime();
  const { capabilities } = useMemoryCapabilities();
  const read = useQuery({
    queryKey: memoryKeys.customers(CUSTOMER_LIST_MAX),
    queryFn: ({ signal }) => {
      if (memory === null) throw new Error("The customer list is read only with a runtime.");
      return memory.api.listCustomers({ limit: CUSTOMER_LIST_MAX }, { signal });
    },
    enabled: memory !== null && capabilities.listCustomers,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return {
    customers: read.data?.customers,
    pending: typing && read.isPending && read.fetchStatus !== "idle",
    failed: typing && read.isError,
  };
}

/** Mounted only while the dialog is open, so the query and the highlight start fresh every time. */
function PaletteBody({
  areas,
  close,
}: Readonly<{ areas: readonly AdminArea[]; close: (didJump: boolean) => void }>) {
  const navigate = useNavigate();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const customerRead = useCustomerRead(query.trim() !== "");
  const results = useMemo(
    () => paletteResults(query, areas, customerRead.customers),
    [query, areas, customerRead.customers],
  );
  const highlighted = Math.min(active, Math.max(results.length - 1, 0));
  const highlightedResult = results[highlighted];

  const choose = (result: PaletteResult | undefined) => {
    if (result === undefined) return;
    close(true);
    navigate(result.to);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      choose(highlightedResult);
      return;
    }
    const next = nextHighlight(event.key, highlighted, results.length);
    if (next === null) return;
    event.preventDefault();
    setActive(next);
  };

  return (
    <>
      <div className="flex items-center gap-2 border-b border-hairline px-4 py-1.5">
        <Icon name="search" size="lg" className="text-muted-foreground" />
        <Input
          autoFocus
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={
            highlightedResult ? optionId(listId, highlightedResult.key) : undefined
          }
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          className="h-12 flex-1 rounded-xl border-0 bg-transparent px-2 compact:h-12 md:text-base focus-visible:-outline-offset-2"
          placeholder="Search pages and customers"
          aria-label="Search anything"
        />
        <kbd className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
          Esc
        </kbd>
      </div>
      <div className="min-h-0 overflow-y-auto p-2">
        <PaletteResults
          listId={listId}
          results={results}
          highlightedKey={highlightedResult?.key}
          onHover={(result) => setActive(results.indexOf(result))}
          onChoose={choose}
        />
        <div role="status" aria-live="polite" className="px-2.5 py-2 text-sm text-muted-foreground">
          {statusLine(results.length, customerRead)}
        </div>
      </div>
    </>
  );
}

const optionId = (listId: string, key: string) => `${listId}-${key}`;

type ResultHandlers = Readonly<{
  onHover: (result: PaletteResult) => void;
  onChoose: (result: PaletteResult) => void;
}>;

/** One listbox with the groups inside; the empty and failure notes sit outside it, in a live region. */
function PaletteResults({
  listId,
  results,
  highlightedKey,
  onHover,
  onChoose,
}: ResultHandlers &
  Readonly<{
    listId: string;
    results: readonly PaletteResult[];
    highlightedKey: string | undefined;
  }>) {
  return (
    <div id={listId} role="listbox" aria-label="Results" className="grid gap-1 compact:gap-2">
      {(["page", "customer"] as const).map((group) => (
        <PaletteGroup
          key={group}
          group={group}
          listId={listId}
          items={results.filter((result) => result.group === group)}
          highlightedKey={highlightedKey}
          onHover={onHover}
          onChoose={onChoose}
        />
      ))}
    </div>
  );
}

function PaletteGroup({
  group,
  listId,
  items,
  highlightedKey,
  onHover,
  onChoose,
}: ResultHandlers &
  Readonly<{
    group: PaletteResult["group"];
    listId: string;
    items: readonly PaletteResult[];
    highlightedKey: string | undefined;
  }>) {
  if (items.length === 0) return null;
  const headingId = `${listId}-${group}`;
  return (
    <div role="presentation" className="grid gap-0.5">
      <span id={headingId} className="px-2.5 pt-2 pb-1 text-xs font-semibold text-muted-foreground">
        {GROUP_LABELS[group]}
      </span>
      {/* biome-ignore lint/a11y/useSemanticElements: a fieldset is a form group; this is a group of options */}
      <div role="group" aria-labelledby={headingId} className="grid gap-0.5">
        {items.map((result) => (
          <PaletteOption
            key={result.key}
            id={optionId(listId, result.key)}
            result={result}
            selected={result.key === highlightedKey}
            onHover={onHover}
            onChoose={onChoose}
          />
        ))}
      </div>
    </div>
  );
}

function PaletteOption({
  id,
  result,
  selected,
  onHover,
  onChoose,
}: ResultHandlers & Readonly<{ id: string; result: PaletteResult; selected: boolean }>) {
  return (
    // The options are reached with the arrow keys from the input (`aria-activedescendant`); the click is for a pointer.
    // biome-ignore lint/a11y/useKeyWithClickEvents: the input owns the keyboard
    <div
      id={id}
      ref={selected ? keepInView : undefined}
      role="option"
      aria-selected={selected}
      tabIndex={-1}
      onPointerMove={() => onHover(result)}
      onClick={() => onChoose(result)}
      className={cn(
        "flex min-h-11 cursor-pointer items-center gap-3 rounded-xl px-2.5 text-sm compact:min-h-8",
        selected && "bg-accent text-accent-foreground",
      )}
    >
      <Icon
        name={result.area === undefined ? "user-group" : navIconFor(result.area)}
        size="md"
        className="text-muted-foreground"
      />
      <span
        className={cn(
          "min-w-0 truncate",
          result.group === "customer" && "font-serif text-base font-medium",
        )}
      >
        {result.label}
      </span>
    </div>
  );
}
